import { useMemo } from 'react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getPolicyDetail } from '../../../services'
import { PolicyDetail } from '../../../types'
import { Loader2 } from 'lucide-react'
import GroupEmployeesTable from '../../Policies/List/group-employees-table'

/** Safely parse a JSON string, return fallback on failure */
function safeJsonParse<T>(value: unknown, fallback: T): T {
    if (typeof value !== 'string') return fallback
    try {
        return JSON.parse(value) as T
    } catch {
        return fallback
    }
}

// Fetches policy detail then renders groups/users/roles with employees
// Needed because /permissions/:id/policies doesn't return groups/users detail
const ExpandedPolicyGroups: React.FC<{ policy: PolicyDetail }> = ({
    policy,
}) => {
    const { data, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.AUTHORIZATION_POLICIES, 'detail', policy.id],
        queryFn: () => getPolicyDetail(policy.id),
    })

    const detail = (data?.result as PolicyDetail | undefined) ?? policy

    const parsedGroups = useMemo(
        () =>
            safeJsonParse<{ id: string; extendChildren: boolean }[]>(
                detail.config?.groups,
                []
            ),
        [detail.config?.groups]
    )

    if (isLoading) {
        return (
            <div className="flex items-center gap-1.5 p-3 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Đang tải...</span>
            </div>
        )
    }

    // Resolved from policy detail API, fallback to parsed config
    const groups = detail.groups?.length ? detail.groups : parsedGroups
    const users = detail.users?.length
        ? detail.users
        : safeJsonParse<string[]>(detail.config?.users, [])
    const roles = detail.roles?.length
        ? detail.roles
        : safeJsonParse<{ id: string; required: boolean }[]>(
              detail.config?.roles,
              []
          )

    const hasContent =
        roles.length > 0 ||
        users.length > 0 ||
        groups.length > 0 ||
        (detail.groupDetails?.length ?? 0) > 0

    if (!hasContent) {
        return (
            <div className="p-3 text-xs text-muted-foreground">
                Không có chi tiết
            </div>
        )
    }

    return (
        <div className="p-3 space-y-3">
            {/* Roles */}
            {roles.length > 0 && (
                <div>
                    <span className="text-xs font-semibold text-muted-foreground">
                        Roles ({roles.length})
                    </span>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                        {roles.map((role) => (
                            <span
                                key={role.id}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200"
                            >
                                {role.id}
                                {role.required && (
                                    <span className="text-red-500">*</span>
                                )}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Users */}
            {users.length > 0 && (
                <div>
                    <span className="text-xs font-semibold text-muted-foreground">
                        Users ({users.length})
                    </span>
                    {detail.userDetails && detail.userDetails.length > 0 ? (
                        <table className="w-full mt-1 text-xs border-collapse">
                            <thead>
                                <tr className="bg-muted/50">
                                    <th className="text-left p-1.5 border font-medium">
                                        Username
                                    </th>
                                    <th className="text-left p-1.5 border font-medium">
                                        Họ tên
                                    </th>
                                    <th className="text-left p-1.5 border font-medium">
                                        Email
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {detail.userDetails.map((user) => (
                                    <tr key={user.id}>
                                        <td className="p-1.5 border">
                                            {user.username}
                                        </td>
                                        <td className="p-1.5 border">
                                            {[user.firstName, user.lastName]
                                                .filter(Boolean)
                                                .join(' ')}
                                        </td>
                                        <td className="p-1.5 border text-muted-foreground">
                                            {user.email || '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                            {users.map((userId) => (
                                <span
                                    key={userId}
                                    className="inline-flex px-2 py-0.5 rounded-full text-xs font-mono bg-green-50 text-green-700 border border-green-200"
                                >
                                    {userId}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Groups — groupDetails (has name) or raw groups, each with employees table */}
            {(detail.groupDetails?.length || groups.length > 0) && (
                <div>
                    <span className="text-xs font-semibold text-muted-foreground">
                        Groups ({detail.groupDetails?.length || groups.length})
                    </span>
                    <div className="space-y-2 mt-1">
                        {detail.groupDetails && detail.groupDetails.length > 0
                            ? detail.groupDetails.map((group) => (
                                  <div
                                      key={group.id}
                                      className="border rounded p-2 bg-background"
                                  >
                                      <div className="flex items-center gap-2 mb-1.5">
                                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-700 border border-purple-200 font-medium">
                                              {group.name}
                                          </span>
                                          <span className="text-xs text-muted-foreground">
                                              {group.path}
                                          </span>
                                          {group.extendChildren && (
                                              <span className="text-xs text-orange-600">
                                                  (+children)
                                              </span>
                                          )}
                                      </div>
                                      <GroupEmployeesTable groupId={group.id} />
                                  </div>
                              ))
                            : groups.map((group) => (
                                  <div
                                      key={group.id}
                                      className="border rounded p-2 bg-background"
                                  >
                                      <div className="flex items-center gap-2 mb-1.5">
                                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-mono bg-purple-50 text-purple-700 border border-purple-200">
                                              {group.id}
                                          </span>
                                          {group.extendChildren && (
                                              <span className="text-xs text-orange-600">
                                                  (+children)
                                              </span>
                                          )}
                                      </div>
                                      <GroupEmployeesTable groupId={group.id} />
                                  </div>
                              ))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ExpandedPolicyGroups
